import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useSelector } from 'react-redux';
import {
	useDismissUserNotificationMutation,
	useGetUserNotificationQuery,
} from '@/features/notifications/notificationsApi';
import { useBannerColours } from '@/hooks/useBannerColours';

const POLLING_INTERVAL = 5 * 60 * 1000;

const FolioUpdateBanner = () => {
	const {
		backgroundColor,
		border,
		textColor: textColour,
		secondaryTextColor: secondaryTextColour,
	} = useBannerColours('info');

	// Get current user from Redux store
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	// Fetch user details using the API
	const { data: userData } = useGetUserDetailsQuery({ email }, { skip: !email });

	const userId = useMemo(() => userData?.userId || userData?.id, [userData]);

	// Check if user has a notification
	const { data: notificationData } = useGetUserNotificationQuery(
		{ userId, notificationType: 'newRelease' },
		{ skip: !userId, pollingInterval: POLLING_INTERVAL },
	);

	const closed =
		localStorage.getItem('hardRefreshFlag') === 'true' || !notificationData?.hasNotification;

	// Mutation to dismiss notification
	const [dismissNotification] = useDismissUserNotificationMutation();

	// Listen for the keyboard shortcuts BEFORE the page unloads
	useEffect(() => {
		const handleKeyDown = (e) => {
			const hasShift = e.shiftKey;
			const hasModifier = e.ctrlKey || e.metaKey; // Handles Ctrl (Win/Linux) and Cmd (Mac)
			const isRKey = e.key === 'r' || e.key === 'R';
			const isF5Key = e.key === 'F5';

			if ((hasModifier && hasShift && isRKey) || (e.ctrlKey && isF5Key)) {
				localStorage.setItem('hardRefreshFlag', 'true');
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	const handleDismiss = async () => {
		if (notificationData?.notification?.id && userId) {
			try {
				const notificationId = notificationData?.notification?.id;
				await dismissNotification({
					notificationId,
					userId,
					notificationType: 'newRelease',
				}).unwrap();
			} catch (error) {
				console.error('Error dismissing notification:', error);
			}
		}
	};

	// Auto-dismiss notification on hard refresh
	useEffect(() => {
		const autoDismiss = async () => {
			const isHardRefresh = localStorage.getItem('hardRefreshFlag') === 'true';
			if (isHardRefresh && notificationData) {
				localStorage.removeItem('hardRefreshFlag');
				if (notificationData?.hasNotification) await handleDismiss();
			}
		};

		autoDismiss();
	}, [notificationData, userId, handleDismiss]);

	if (closed) {
		return null;
	}

	return (
		<Box px='1.5em'>
			<Box
				sx={{
					position: 'relative',
					width: '100%',
					padding: 2.5,
					marginTop: '1em',
					backgroundColor,
					borderRadius: '12px',
					border,
				}}
			>
				<IconButton
					onClick={handleDismiss}
					sx={{
						position: 'absolute',
						top: 12,
						right: 12,
						color: secondaryTextColour,
						padding: '4px',
					}}
				>
					<CloseIcon sx={{ fontSize: '18px' }} />
				</IconButton>
				<Typography
					variant='body1'
					sx={{
						fontWeight: 700,
						color: textColour,
						mb: 0.5,
						pr: 3,
					}}
				>
					{"We've made some updates! Please refresh your page 🚀"}
				</Typography>
				<Typography variant='body1' sx={{ color: secondaryTextColour, pr: 3 }}>
					Please use <strong>Ctrl + Shift + R</strong> (or{' '}
					<strong>Cmd + Shift + R</strong> on Mac) to perform a hard refresh to see the
					latest version of Folio.
				</Typography>
			</Box>
		</Box>
	);
};

export default FolioUpdateBanner;
